import React, { useState, useEffect, useCallback, useRef } from "react";
import { secureGet, securePost } from "../../config/api";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import DashboardContent from "./DashboardContent";
import Filter from "../../components/Filter";
import useIsMounted from "../../hooks/useIsMounted";
import LoadingLogo from "../../components/LoadingLogo";

import ChevronDownIcon from "../../icons/chevron-down.svg";

const Dashboard = () => {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [, setApplyFilters] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const controllerRef = useRef(new AbortController());
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const buildRequestBody = useCallback((customFilters = filterValues) => {
    const requestBody = {};

    Object.entries(customFilters).forEach(([key, value]) => {
      if (key === "Date" && value && typeof value === "object") {
        if (value.startDate && value.endDate) {
          requestBody["startDate"] = value.startDate;
          requestBody["endDate"] = value.endDate;
        }
      } else if (Array.isArray(value)) {
        const cleanedValues = value.filter((v) => v !== "SELECT_ALL");
        requestBody[key] = cleanedValues.length > 0 ? cleanedValues : undefined;
      } else if (value !== undefined) {
        requestBody[key] = value;
      }
    });

    return requestBody;
  }, []);


  const fetchDashboardContent = useCallback(async (dashboardId, baseDashboard, overrideFilters = null) => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;



    setLoading(true);
    try {
      const requestBody = buildRequestBody(overrideFilters ?? filterValues);
      console.log("requestBody", requestBody);
      const response = await securePost(`/dashboard/${dashboardId}`, requestBody, { signal });

      if (isMounted.current) {
        setSelectedDashboard({
          ...baseDashboard,
          graphs: response.data.graphs || [],
        });
      }
    } catch (error) {
      if (error.name === "AbortError") {
      } else {
        console.error(`Failed to fetch content for dashboard ${dashboardId}:`, error);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [filterValues, isMounted]);


  const fetchDashboards = useCallback(async () => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      const response = await secureGet("/dashboard", { signal });
      const sortedDashboards = response.data.sort((a, b) => a.order - b.order);

      if (isMounted.current) {
        setDashboards(sortedDashboards);
        if (sortedDashboards.length > 0) {
          const firstDashboard = sortedDashboards[0];
          setSelectedDashboard(firstDashboard);
          const initialFilters = getInitialFilters(firstDashboard);
          setFilterValues(initialFilters);
          await fetchDashboardContent(firstDashboard.id, firstDashboard, initialFilters);

        }
        setLoading(false);
      }
    } catch (error) {
      if (error.name === "AbortError") {
      } else {
        console.error("Failed to fetch dashboards:", error);
      }
    }
  }, [fetchDashboardContent, isMounted]);

  useEffect(() => {
    fetchDashboards();

    return () => {
      controllerRef.current.abort();
    };
  }, []);



  const handleDashboardChange = (id) => {
    const newDashboard = dashboards.find((dashboard) => dashboard.id === id);
    if (newDashboard) {
      setSelectedDashboard(newDashboard);
      setFilterValues(getInitialFilters(newDashboard));
      fetchDashboardContent(id, newDashboard);
    }
  };

  const formatDateForPayload = (date, isEndDate = false) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    
    // For date ranges: start date = 00:00:00, end date = 23:59:59
    const hours = isEndDate ? "23" : "00";
    const minutes = isEndDate ? "59" : "00";
    const seconds = isEndDate ? "59" : "00";
    
    const timezoneOffset = -date.getTimezoneOffset();
    const absOffset = Math.abs(timezoneOffset);
    const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const offsetMinutes = String(absOffset % 60).padStart(2, "0");
    const timezone = `${timezoneOffset >= 0 ? "+" : "-"}${offsetHours}${offsetMinutes}`;

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezone}`;
  };

  const getInitialFilters = (dashboard) => {
    const initialFilters = {};
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);

    dashboard.filters?.forEach((filter) => {
      if (filter.type === "DATE_RANGE_PICKER") {
        initialFilters[filter.queryParameter] = {
          startDate: formatDateForPayload(sevenDaysAgo, false),
          endDate: formatDateForPayload(today, true),
        };
      } else if (filter.type === "OBJECT" && filter.multiselect && filter.results?.length > 0) {
        initialFilters[filter.queryParameter] = [
          "SELECT_ALL",
          ...filter.results.map((item) => item.id || item.value),
        ];
      }
    });

    return initialFilters;
  };


  const handleFilterChange = (name, value) => {
    setFilterValues((prev) => {
      if (typeof value === "object" && value.startDate && value.endDate) {
        return { ...prev, [name]: value };
      }

      const allOptions = processedFilters.find((filter) => filter.name === name)?.options || [];
      const allValues = allOptions.map((option) => option.value).filter((val) => val !== "SELECT_ALL");

      if (Array.isArray(value)) {
        if (value.includes("SELECT_ALL")) {
          return { ...prev, [name]: ["SELECT_ALL", ...allValues] };
        } else {
          return { ...prev, [name]: value.filter((v) => v !== "SELECT_ALL") };
        }
      }

      return { ...prev, [name]: value || undefined };
    });
  };

  const resetFilters = () => {
    if (selectedDashboard) {
      const initialFilters = getInitialFilters(selectedDashboard);
      setFilterValues(initialFilters);
      fetchDashboardContent(selectedDashboard.id, selectedDashboard, initialFilters);
    } else {
      setFilterValues({});
    }
  };

  const applyFilterChanges = () => {
    if (selectedDashboard) {
      fetchDashboardContent(selectedDashboard.id, selectedDashboard);
      setApplyFilters(true);
    }
  };

  const preprocessFilters = (filters, isDashboard = false) => {
    return filters.map((filter) => {
      let options = [];
      let defaultValue = [];

      if (filter.type === "USER" && filter.results) {
        options = filter.results.map((user) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`,
        }));
      } else if (filter.type === "CLIENT" && filter.results) {
        options = filter.results.map((client) => ({
          value: client.id,
          reference: client.code,
          name: client.name,
        }));
      } else if (filter.type === "OBJECT" && filter.results) {
        options = filter.results.map((brand) => ({
          value: brand.id,
          label: brand.name,
        }));
        if (isDashboard && filter.multiselect && options.length > 0) {
          options.unshift({
            value: "SELECT_ALL",
            label: t("placeholder.selectAll"),
          });

          defaultValue = options.map((option) => option.value).filter((val) => val !== "SELECT_ALL");
        }
      } else if (filter.type === "STRING" && filter.results) {
        options = filter.results.map((zone) => ({
          value: zone,
          label: zone,
        }));
      } else if (filter.type === "PRODUCT" && Array.isArray(filter.results)) {
        options = filter.results.map((product) => ({
          value: product.id,
          reference: product.reference,
          name: product.name,
        }));
      } else if (filter.results && Array.isArray(filter.results)) {
        options = filter.results.map((item) => ({
          value: item.value || item.id || item,
          label: item.label || item.name || item,
        }));
      }
      return {
        name: filter.queryParameter,
        type:
          filter.type === "DATE_RANGE_PICKER"
            ? "daterange"
            : filter.type === "OBJECT" && filter.multiselect
              ? "checkbox-dropdown"
              : "dropdown",
        label: filter.type === "DATE_RANGE_PICKER" ? "" : filter.title,
        options: options || [],
        defaultValue,
      };
    });
  };

  const processedFilters =
    selectedDashboard?.filters
      ? preprocessFilters(selectedDashboard.filters, true).map((filter) => ({
        ...filter,
        isDashboardFilter: true,
      }))
      : [];

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.dashboard")}</title>
      </Helmet>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-[24px] font-semibold text-[#6A78F8]">{t(`titles.dashboard_details`)}</div>
      </div>

      {/* Dashboards Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 text-[32px] font-semibold text-black"
        >
          {selectedDashboard?.title || "Dashboards"}
          <img
            src={ChevronDownIcon}
            alt="Toggle"
            className={`w-6 h-6 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""
              }`}
          />
        </button>

        {isDropdownOpen && (
          <div className="absolute mt-2 bg-white shadow-md rounded-md z-50">
            {dashboards.map((dashboard, index) => (
              <div key={dashboard.id}>
                <button
                  className={`block w-full pl-4 pr-8 py-2 text-left hover:bg-gray-100 ${dashboard.id === selectedDashboard?.id
                      ? "text-[#6A78F8] font-bold text-[16px]"
                      : "text-[#38485C] font-medium"
                    }`}
                  onClick={() => {
                    handleDashboardChange(dashboard.id);
                    setIsDropdownOpen(false);
                  }}
                >
                  {dashboard.title}
                </button>
                {index < dashboards.length - 1 && (
                  <div className="flex justify-center">
                    <div className="w-[80%] h-[1px] bg-[#F7F7F7]"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>



      {processedFilters.length > 0 && (
        <div className="mt-6">
          <Filter
            filters={processedFilters}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            resetFilters={resetFilters}
            applyFilterChanges={applyFilterChanges}
            showApplyButton={true}
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (
        selectedDashboard?.graphs && (
          <div className="mt-8">
            <DashboardContent
              graphs={selectedDashboard.graphs}
              dashboardId={selectedDashboard.id}
              filters={buildRequestBody(filterValues)}
            />
          </div>
        )
      )}
    </div>
  );
};

export default Dashboard;
